import { useRef } from 'react';
import useRateCalc from '../../../application/bloc/useRateCalc';
import useRepaymentInfoBottomSheet from '../../../application/hooks/useRepaymentInfoBottomSheet';

import {
  HeadTitle,
  TextField,
  Spacer,
  SegmentButtons,
  Button,
  TheRateCalcTable,
  Tmd16,
  Bottomsheet,
} from 'oui';
import { FlexBox, FloatingBox, ListWithGap, SideBox } from '../LBox/LBox';
import RepaymentInfoBottomSheet from '../../feedback/RepaymentInfoBottomSheet/RepaymentInfoBottomSheet';
import {
  currency,
  currencyKR,
  decimalNumber,
} from '../../../application/helper/filter';

interface IRateCalculator {
  onIconClick?: () => void;
  onInfoIconClick?: () => void;
  isBottomSheet?: boolean;
  isInputBottomSheet?: boolean;
  preset?: {
    amount?: string;
    interestRate?: string;
    term?: string;
    maxTerm?: number;
  };
  showInput?: {
    amountInput?: boolean;
    interestRateInput?: boolean;
    termInput?: boolean;
  };
}

const RateCalculator = ({
  onIconClick,
  onInfoIconClick,
  isBottomSheet,
  isInputBottomSheet = false,
  preset,
  showInput: {
    amountInput = true,
    interestRateInput = true,
    termInput = true,
  } = {},
}: IRateCalculator) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const {
    repayment,
    isRepayment,
    inputs,
    calculatedData,
    result,
    loanAmountWarningText,
    annualInterestWarningText,
    loanTermWarningText,
    focusedInput,
    isTableFolded,
    isInputBottomSheetOpen,
    handleClickCalculate,
    handleClickTab,
    handleInputChange,
    handleFocusTextField,
    handleFocusedInput,
    handleBlurTextField,
    handleMouseDown,
    handleIsTableFolded,
    inputRef,
    inputRef2,
    inputRef3,
    sendBeaconClkBtnHelp,
    sendBeaconClkBtnCalc,
    sendBeaconClkBtnCalcOutlink,
    hasAppbar,
  } = useRateCalc(preset);

  const {
    openInfoBottomSheet,
    handleClickOpenInfoBottom,
    handleClickCloseInfoBottom,
  } = useRepaymentInfoBottomSheet();

  const handleClickHelper = () => {
    sendBeaconClkBtnHelp('도움말');
    onInfoIconClick ? onInfoIconClick() : handleClickOpenInfoBottom();
  };

  const handleClickMoveToTargetRef = () => {
    setTimeout(() => {
      if (!targetRef.current || !contentRef.current) return;

      const headerOffset = 52;

      const content = isBottomSheet ? contentRef.current : window;

      const top = isBottomSheet
        ? targetRef.current.offsetTop - contentRef.current.offsetTop
        : targetRef.current.getBoundingClientRect().top +
          window.scrollY -
          headerOffset;

      content.scrollTo({
        top,
        behavior: 'smooth',
      });
    }, 0);
  };

  const tabsItems = [
    { label: '원리금균등', value: 0 },
    { label: '원금균등', value: 1 },
    { label: '만기일시', value: 2 },
  ];

  const hasResult =
    Object.keys(result).some(Boolean) && calculatedData.length !== 0;

  return (
    <>
      <HeadTitle
        textTitle={'이자 계산기'}
        buttonIconName={isBottomSheet ? 'CloseSharp' : undefined}
        onClickButton={onIconClick}
        size="L"
      />
      <div
        ref={contentRef}
        style={{
          overflowY: 'scroll',
          height: isBottomSheet ? `inherit` : ``,
        }}
      >
        <SideBox>
          {amountInput && (
            <TextField
              inputTitle="대출 금액"
              inputMode="numeric"
              state={inputs.loanAmount ? 'focused' : 'enabled'}
              adornment={'만 원'}
              helperText={
                inputs.loanAmount && currencyKR(+inputs?.loanAmount * 10000)
              }
              placeholder={'0'}
              value={inputs?.loanAmount ? currency(+inputs?.loanAmount) : ''}
              warning={loanAmountWarningText}
              name={'loanAmount'}
              onClickTextField={
                isInputBottomSheet
                  ? () => handleFocusedInput('loanAmount')
                  : undefined
              }
              onChangeTextInput={
                isInputBottomSheet
                  ? undefined
                  : (value) => handleInputChange(value, 'loanAmount')
              }
              scrollIntoViewOnFocus={!isBottomSheet}
              ref={inputRef}
              readOnly={isInputBottomSheet}
            />
          )}
          {interestRateInput && (
            <>
              <Spacer size={'md'} />
              <TextField
                inputTitle="연 금리"
                inputMode="decimal"
                state={inputs.annualInterest ? 'focused' : 'enabled'}
                adornment={'%'}
                placeholder="0"
                value={
                  inputs?.annualInterest
                    ? decimalNumber(inputs?.annualInterest)
                    : ''
                }
                warning={annualInterestWarningText}
                name={'annualInterest'}
                onClickTextField={
                  isInputBottomSheet
                    ? () => handleFocusedInput('annualInterest')
                    : undefined
                }
                onChangeTextInput={
                  isInputBottomSheet
                    ? undefined
                    : (value) => handleInputChange(value, 'annualInterest')
                }
                scrollIntoViewOnFocus={!isBottomSheet}
                ref={inputRef2}
                readOnly={isInputBottomSheet}
              />
            </>
          )}
          {termInput && (
            <>
              <Spacer size={'md'} />
              <TextField
                inputTitle="대출 기간"
                inputMode="numeric"
                state={inputs.loanTerm ? 'focused' : 'enabled'}
                adornment={'개월'}
                placeholder="0"
                value={inputs?.loanTerm ? currency(+inputs?.loanTerm) : ''}
                warning={loanTermWarningText}
                name={'loanTerm'}
                onClickTextField={
                  isInputBottomSheet
                    ? () => handleFocusedInput('loanTerm')
                    : undefined
                }
                onChangeTextInput={
                  isInputBottomSheet
                    ? undefined
                    : (value) => handleInputChange(value, 'loanTerm')
                }
                scrollIntoViewOnFocus={!isBottomSheet}
                ref={inputRef3}
                readOnly={isInputBottomSheet}
              />
            </>
          )}
        </SideBox>
        <Spacer size="md" />
        {hasAppbar && <div ref={targetRef} />}
        <HeadTitle
          textTitle={'상환 방식'}
          fixed={true}
          buttonIconName={'InfoOutlined'}
          onClickButton={handleClickHelper}
          size="M"
        />
        <SideBox padding="0px 20px 20px">
          <SegmentButtons
            items={tabsItems}
            activeIdx={repayment}
            onClickButton={handleClickTab}
            varient="b"
          />
        </SideBox>
        {hasResult && (
          <>
            <ListWithGap gap={8}>
              <FlexBox justify="space-between">
                <Tmd16 text="총 상환 금액" color="#696969" />
                <Tmd16 text={result.totalRepaymentAmount} color="#4B2CE8" />
              </FlexBox>
              <FlexBox justify="space-between">
                <Tmd16 text="총 대출 이자" color="#696969" />
                <Tmd16 text={result.totalLoanInterest} color="#4B2CE8" />
              </FlexBox>
              <FlexBox justify="space-between">
                <Tmd16 text="1회차 상환 금액" color="#696969" />
                <Tmd16
                  text={`${parseInt(
                    calculatedData[0].monthlyPayment,
                  ).toLocaleString()} 원`}
                  color="#4B2CE8"
                />
              </FlexBox>
            </ListWithGap>

            <HeadTitle textTitle={'월 상환 금액'} />
            <TheRateCalcTable
              dataList={
                isTableFolded ? calculatedData.slice(0, 12) : calculatedData
              }
            />
          </>
        )}
        {calculatedData.length > 12 && (
          <SideBox padding="20px 20px 0">
            <Button
              size="md"
              configuration="lightContained"
              onClick={() => handleIsTableFolded(!isTableFolded)}
            >
              회차별 상환금액 {isTableFolded ? '더 보기' : '접기'}
            </Button>
          </SideBox>
        )}
        <Spacer size={'lg'} />
        {!isInputBottomSheet && (
          <>
            <Spacer size={'lg'} />
            <Spacer size={'lg'} />
          </>
        )}
      </div>
      {isInputBottomSheet ? (
        <Bottomsheet
          backdrop
          isOpen={isInputBottomSheetOpen}
          isMaxHeight={false}
        >
          <HeadTitle
            textTitle="대출이자 계산기"
            buttonIconName={'CloseSharp'}
            onClickButton={() => handleFocusedInput('')}
            size="M"
          />
          <Spacer size="lg" />
          <SideBox>
            {amountInput && (
              <TextField
                state={inputs.loanAmount ? 'focused' : 'enabled'}
                adornment={'만 원'}
                helperText={currencyKR(+inputs?.loanAmount * 10000)}
                placeholder={'대출 금액'}
                onChangeTextInput={(value) =>
                  handleInputChange(value, 'loanAmount')
                }
                inputMode={'numeric'}
                value={inputs?.loanAmount ? currency(+inputs?.loanAmount) : ''}
                warning={loanAmountWarningText}
                name={'loanAmount'}
                onFocus={handleFocusTextField}
                onBlur={handleBlurTextField}
                scrollIntoViewOnFocus={false}
                autoFocus={focusedInput === 'loanAmount'}
              />
            )}
            {interestRateInput && (
              <>
                <Spacer size={'md'} />
                <TextField
                  state={inputs.annualInterest ? 'focused' : 'enabled'}
                  adornment={'%'}
                  placeholder={'연 금리'}
                  onChangeTextInput={(value) =>
                    handleInputChange(value, 'annualInterest')
                  }
                  inputMode={'decimal'}
                  value={
                    inputs?.annualInterest
                      ? decimalNumber(inputs?.annualInterest)
                      : ''
                  }
                  warning={annualInterestWarningText}
                  name={'annualInterest'}
                  onFocus={handleFocusTextField}
                  onBlur={handleBlurTextField}
                  scrollIntoViewOnFocus={false}
                  autoFocus={focusedInput === 'annualInterest'}
                />
              </>
            )}
            {termInput && (
              <>
                <Spacer size={'md'} />
                <TextField
                  state={inputs.loanTerm ? 'focused' : 'enabled'}
                  adornment={'개월'}
                  placeholder={'대출 기간'}
                  onChangeTextInput={(value) =>
                    handleInputChange(value, 'loanTerm')
                  }
                  inputMode={'numeric'}
                  value={inputs?.loanTerm ? currency(+inputs?.loanTerm) : ''}
                  warning={loanTermWarningText}
                  name={'loanTerm'}
                  onFocus={handleFocusTextField}
                  onBlur={handleBlurTextField}
                  scrollIntoViewOnFocus={false}
                  autoFocus={focusedInput === 'loanTerm'}
                />
              </>
            )}
            <Spacer size="md" />
            <Spacer size="sm" />
            <Button
              state={
                inputs.loanAmount &&
                inputs.loanAmount !== '0' &&
                inputs.loanTerm &&
                inputs.loanTerm !== '0' &&
                inputs.annualInterest &&
                inputs.annualInterest !== '0' &&
                !loanTermWarningText &&
                !loanAmountWarningText &&
                isRepayment
                  ? 'enabled'
                  : 'disabled'
              }
              flexible={true}
              configuration={'contained'}
              onClick={() => {
                sendBeaconClkBtnCalc('계산하기');
                handleClickCalculate(
                  repayment,
                  +inputs.loanAmount,
                  +inputs.loanTerm,
                  +inputs.annualInterest,
                );
                handleClickMoveToTargetRef();
                handleFocusedInput('');
              }}
              children={'다음'}
              isDisabled={
                !(
                  inputs.loanAmount &&
                  inputs.loanTerm &&
                  inputs.annualInterest &&
                  !loanTermWarningText &&
                  !loanAmountWarningText &&
                  isRepayment
                )
              }
              //@ts-ignore
              onMouseDown={handleMouseDown}
            />
            <Spacer size="lg" />
          </SideBox>
        </Bottomsheet>
      ) : (
        <FloatingBox>
          <Button
            state={
              inputs.loanAmount &&
              inputs.loanAmount !== '0' &&
              inputs.loanTerm &&
              inputs.loanTerm !== '0' &&
              inputs.annualInterest &&
              inputs.annualInterest !== '0' &&
              !loanTermWarningText &&
              !loanAmountWarningText &&
              isRepayment
                ? 'enabled'
                : 'disabled'
            }
            flexible={true}
            configuration={'contained'}
            onClick={() => {
              sendBeaconClkBtnCalc('계산하기');
              handleClickCalculate(
                repayment,
                +inputs.loanAmount,
                +inputs.loanTerm,
                +inputs.annualInterest,
              );
              handleClickMoveToTargetRef();
              handleFocusedInput('');
            }}
            children={'계산하기'}
            isDisabled={
              !(
                inputs.loanAmount &&
                inputs.loanTerm &&
                inputs.annualInterest &&
                !loanTermWarningText &&
                !loanAmountWarningText &&
                isRepayment
              )
            }
            //@ts-ignore
            onMouseDown={handleMouseDown}
          />
        </FloatingBox>
      )}
      <RepaymentInfoBottomSheet
        isShow={openInfoBottomSheet}
        handleClickCloseInfoBottom={handleClickCloseInfoBottom}
        isBottomSheet={true}
      />
    </>
  );
};

export default RateCalculator;
